import React, { useCallback, useRef } from 'react';
import {
  faArrowRightToBracket,
  faEllipsisVertical,
  faMoon,
  faSliders,
} from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@/shared/utils/cn';
import { Avatar } from '@/modules/Avatar';
import type { AvatarProps } from '@/modules/Avatar/types';
import { Badge } from '@/modules/Badge';
import { Button } from '@/modules/Button';
import { ListItemDefault } from '@/modules/Lists';
import { Popup } from '@/modules/Popup';
import type { PopupRef } from '@/shared/types/popup';
import { Toggle } from '@/modules/Toggle';
import type {
  SidebarUserProfileMenuProps,
  SidebarUserProfileProps,
  SidebarUserProfileSummaryProps,
} from '../../types';
import { SidebarDivider } from '../atoms';

function renderAvatar(
  size: AvatarProps['size'],
  avatarProps?: SidebarUserProfileSummaryProps['avatarProps']
) {
  const avatarType = avatarProps?.type ?? 'people';
  const { type: _t, ...restAvatarProps } = avatarProps ?? {};
  return <Avatar {...({ type: avatarType, size, ...restAvatarProps } as AvatarProps)} />;
}

interface SidebarUserProfilePopoverPanelProps {
  summary: SidebarUserProfileSummaryProps;
  menu: SidebarUserProfileMenuProps;
  onRequestClose: () => void;
}

function SidebarUserProfilePopoverPanel({
  summary,
  menu,
  onRequestClose,
}: SidebarUserProfilePopoverPanelProps) {
  const {
    onSettings,
    settingsLabel = 'Configuración',
    darkMode,
    onLogout,
    logoutLabel = 'Cerrar sesión',
  } = menu;

  const headerSecondLine = summary.email ?? summary.subtitle;

  const handleSettingsItemClick = useCallback(
    (_event: React.MouseEvent) => {
      onSettings?.();
      onRequestClose();
    },
    [onRequestClose, onSettings]
  );

  const handleLogoutItemClick = useCallback(
    (_event: React.MouseEvent) => {
      onLogout?.();
      onRequestClose();
    },
    [onLogout, onRequestClose]
  );

  const showLogout = Boolean(onLogout);

  return (
    <div className="ui-flex ui-min-w-[220px] ui-max-w-[280px] ui-flex-col ui-gap-8 ui-p-8">
      <div
        className={cn(
          'ui-flex ui-items-center ui-gap-8 ui-rounded-control-sm',
          'ui-bg-surface-secondary ui-p-8'
        )}
      >
        {renderAvatar('sm', summary.avatarProps)}
        <div className="ui-min-w-0 ui-flex-1">
          <div className="ui-truncate ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary">
            {summary.name}
          </div>
          {headerSecondLine ? (
            <div className="ui-truncate ui-text-body-xs-regular ui-text-secondary">
              {headerSecondLine}
            </div>
          ) : null}
        </div>
        {summary.presence ? (
          <Badge
            label={summary.presence.label}
            color="success"
            theme="soft"
            size="md"
            stroke
            className="ui-shrink-0"
          />
        ) : null}
      </div>

      {onSettings || darkMode ? (
        <ul className="ui-m-0 ui-flex ui-list-none ui-flex-col ui-gap-0 ui-p-0">
          {onSettings ? (
            <ListItemDefault
              icon={faSliders}
              color="blue"
              onClick={handleSettingsItemClick}
              className="ui-h-32"
            >
              <span className="ui-flex-1 ui-truncate ui-font-body-sm-medium">{settingsLabel}</span>
            </ListItemDefault>
          ) : null}
          {darkMode ? (
            <ListItemDefault icon={faMoon} color="blue" className="ui-h-32 !ui-cursor-default">
              <span className="ui-flex ui-min-w-0 ui-flex-1 ui-items-center ui-justify-between ui-gap-8">
                <span className="ui-truncate ui-font-body-sm-medium">
                  {darkMode.label ?? 'Dark mode'}
                </span>
                <span
                  className="ui-inline-flex ui-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Toggle
                    checked={darkMode.checked}
                    onChange={darkMode.onChange}
                    ariaLabel={darkMode.label ?? 'Dark mode'}
                    tooltip={false}
                  />
                </span>
              </span>
            </ListItemDefault>
          ) : null}
        </ul>
      ) : null}

      {showLogout ? (
        <>
          <SidebarDivider />
          <ul className="ui-m-0 ui-flex ui-list-none ui-flex-col ui-gap-0 ui-p-0">
            <ListItemDefault
              icon={faArrowRightToBracket}
              color="blue"
              iconColor="danger"
              onClick={handleLogoutItemClick}
              className="ui-h-32 !ui-text-danger hover:!ui-text-danger"
            >
              <span className="ui-flex-1 ui-truncate ui-font-body-sm-medium ui-text-danger">
                {logoutLabel}
              </span>
            </ListItemDefault>
          </ul>
        </>
      ) : null}
    </div>
  );
}

/**
 * Sidebar user rail (avatar, name, subtitle) with an optional account menu in a border popover
 * without arrow (`popoverArrow={false}`), aligned with Sidebar-Menu Figma `290:52422`.
 */
export const SidebarUserProfile: React.FC<SidebarUserProfileProps> = ({
  summary,
  menu,
  menuPosition = 'top-left',
  className,
}) => {
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<PopupRef>(null);

  const hasMenu = Boolean(menu) && Boolean(menu?.onSettings || menu?.darkMode || menu?.onLogout);

  const handleRequestClose = useCallback(() => {
    popupRef.current?.close();
  }, []);

  return (
    <div className={cn('ui-flex ui-w-full ui-flex-col ui-gap-8', className)}>
      <SidebarDivider />

      <div
        className={cn(
          'ui-flex ui-w-full ui-items-center ui-gap-8',
          'ui-rounded-control-sm ui-bg-surface-default ui-px-8 ui-py-6'
        )}
      >
        {renderAvatar('md', summary.avatarProps)}
        <div className="ui-min-w-0 ui-flex-1">
          <div className="ui-truncate ui-text-body-sm-regular ui-font-body-sm-medium ui-text-primary">
            {summary.name}
          </div>
          {summary.subtitle ? (
            <div className="ui-truncate ui-text-body-xs-regular ui-text-secondary">
              {summary.subtitle}
            </div>
          ) : null}
        </div>

        {hasMenu && menu ? (
          <>
            <Button
              ref={menuTriggerRef}
              type="button"
              color="tertiary"
              size="xs"
              pill="pill"
              borderable={false}
              aria-label="Account menu"
              aria-haspopup="dialog"
              iconProps={{ icon: faEllipsisVertical, show: true }}
            />
            <Popup
              ref={popupRef}
              triggerRef={menuTriggerRef}
              position={menuPosition}
              popoverArrow={false}
              closeOnClick={false}
              closeOnClickOutside
              closeOnEscape
              className="ui-min-w-[220px]"
            >
              <SidebarUserProfilePopoverPanel
                summary={summary}
                menu={menu}
                onRequestClose={handleRequestClose}
              />
            </Popup>
          </>
        ) : null}
      </div>
    </div>
  );
};

SidebarUserProfile.displayName = 'SidebarUserProfile';
